import React, { useState, useEffect, useCallback } from "react";
import InvoiceTable, { Invoice } from "./Components/InvoiceTable/InvoiceTable";
import InvoiceHeader from "./Components/InvoiceTable/InvoiceHeader";
import StatusFilter from "./Components/StatusFilter/StatusFilter";
import "./InvoicePage.css";
import { useParams, useSearchParams } from "react-router-dom";
import InvoiceService from "../../../../AppSettings/services/InvoiceService";

type StatusValue = "Un-Paid" | "Partial" | "Paid" | "Void" | null;

type ViewMode = "table" | "card";

const InvoicePage: React.FC = () => {
  const [showCreateInvoice, setShowCreateInvoice] = useState(false);
  const [invoices, setInvoices] = useState<Invoice[]>([]);
  const [filteredInvoices, setFilteredInvoices] = useState<Invoice[]>([]);
  const [statusFilter, setStatusFilter] = useState<StatusValue>(null);
  const [loading, setLoading] = useState(true);
  const [viewMode, setViewMode] = useState<ViewMode>("card");
  const { matterId } = useParams<{ matterId: string }>();
  const [searchParams] = useSearchParams();

  const columns: Record<string, keyof Invoice | string> = {
    "Invoices": "invoiceid",
    "Created At": "created_at",
    "Due Date": "due_date",
    "Total Amount": "total_amount",
    "Amount Paid": "amount_paid",
  };

  const fetchInvoices = useCallback(async () => {
    try {
      setLoading(true);
      if (matterId) {
        const data = await InvoiceService.getInvoicesByMatterId(matterId);
        setInvoices(data);
        setFilteredInvoices(data);
      } else {
        setInvoices([]);
        setFilteredInvoices([]);
      }
    } catch (err) {
      console.error("Failed to fetch invoices:", err);
      setInvoices([]);
      setFilteredInvoices([]);
    } finally {
      setLoading(false);
    }
  }, [matterId]);

  useEffect(() => {
    fetchInvoices();
  }, [fetchInvoices]);

  useEffect(() => {
    if (searchParams.get("create") === "true") {
      setShowCreateInvoice(true);
    }
  }, [searchParams]);

  const deriveInvoiceStatus = (invoice: Invoice): StatusValue => {
    if (invoice.status === "void") return "Void";
    const amountPaid = parseFloat((invoice.amount_paid as string) || "0");
    const totalAmount = parseFloat((invoice.total_amount as string) || "0");
    // "Paid" = nothing still owed. amountPaid >= totalAmount also covers a $0
    // invoice (0 >= 0), which the old `totalAmount > 0` guard left stuck (M4).
    if (amountPaid >= totalAmount) return "Paid";
    if (amountPaid > 0) return "Partial";
    return "Un-Paid";
  };

  const applyStatusFilter = (list: Invoice[]): Invoice[] => {
    if (statusFilter) {
      return list.filter((invoice) => deriveInvoiceStatus(invoice) === statusFilter);
    }
    // "All" hides voided invoices; user must click "Void" to see them
    return list.filter((invoice) => invoice.status !== "void");
  };

  useEffect(() => {
    if (!invoices) return;
    setFilteredInvoices(applyStatusFilter([...invoices]));
  }, [invoices, statusFilter]);

  const handleSearch = (search: string, filter?: string | null) => {
    if (!filter || !search) {
      if (invoices) {
        setFilteredInvoices(applyStatusFilter([...invoices]));
      }
      return;
    }

    let filtered: Invoice[];
    if (filter === "Invoices") {
      filtered = invoices.filter((invoice) => {
        const invoiceId = (invoice.invoiceid || "").toString().toLowerCase();
        const searchLower = search.toLowerCase();
        return invoiceId.includes(searchLower);
      });
    } else {
      filtered = invoices.filter((invoice) => {
        const columnKey = columns[filter];
        const fieldValue = (invoice as Record<string, unknown>)[columnKey as string];
        if (fieldValue == null) return false;
        return fieldValue.toString().toLowerCase().includes(search.toLowerCase());
      });
    }

    setFilteredInvoices(applyStatusFilter(filtered));
  };

  const handleStatusFilterChange = (status: StatusValue | string | null) => {
    setStatusFilter(status as StatusValue);
  };

  const handleInvoiceCreated = async () => {
    setShowCreateInvoice(false);
    setStatusFilter(null);
    await new Promise(r => setTimeout(r, 500));
    await fetchInvoices();
  };

  return (
    <div>
      <InvoiceHeader 
        onCreateInvoice={() => setShowCreateInvoice(true)}
        viewMode={viewMode}
        onViewModeChange={setViewMode}
      />
      <StatusFilter
        activeStatus={statusFilter}
        onStatusChange={handleStatusFilterChange}
        allData={invoices}
      />
      <InvoiceTable
        showCreateInvoice={showCreateInvoice}
        invoices={filteredInvoices}
        onInvoiceCreated={handleInvoiceCreated}
        loading={loading}
        matterId={matterId}
        viewMode={viewMode}
      />
    </div>
  );
};

export default InvoicePage;
